import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Building2, Zap, ExternalLink } from "lucide-react";

/**
 * Stripe Connect onboarding + status card, embedded on the Payouts page.
 */
export default function ConnectCard() {
    const { user, refresh } = useAuth();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [params, setParams] = useSearchParams();

    const load = async () => {
        try {
            const { data } = await api.get("/connect/status");
            setStatus(data);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (params.get("connect") === "done") {
            toast.success("Bank details submitted — verifying with Stripe");
            setParams({});
            load();
            refresh();
        }
    }, [params, refresh, setParams]);

    const onboard = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
            window.location.href = data.onboarding_url;
        } catch (e) {
            toast.error(formatApiError(e));
            setBusy(false);
        }
    };

    const instantPayout = async () => {
        if (!window.confirm(`Payout $${(user?.tip_balance_usd || 0).toFixed(2)} to your bank via Stripe?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/tips/instant-payout");
            toast.success(`Sent $${data.amount_usd.toFixed(2)} — Stripe payout in progress`);
            refresh();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const ready = status?.payouts_enabled;
    const connected = status?.connected;

    return (
        <section className="brut-static bg-white rounded-xl p-5 mb-8" data-testid="connect-card">
            <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5" />
                <h2 className="display text-xl font-black">Instant bank payouts</h2>
                {ready && (
                    <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-[#0A0A0A] bg-[#FFE55C] text-[10px] font-black uppercase" data-testid="connect-ready">
                        Connected
                    </span>
                )}
            </div>

            {!connected && (
                <>
                    <p className="text-sm text-[#4A4A4A] font-medium mb-3">
                        Skip the manual payout queue — connect your bank via <b>Stripe Connect</b> and cash out any tip balance $10+ straight to your account, any time.
                    </p>
                    <button
                        onClick={onboard}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold text-sm"
                        data-testid="connect-start"
                    >
                        <ExternalLink className="w-4 h-4" /> Connect with Stripe
                    </button>
                </>
            )}

            {connected && !ready && (
                <>
                    <p className="text-sm text-[#4A4A4A] font-medium mb-3">
                        Stripe still needs a bit more info to enable payouts. Finish onboarding to unlock instant cashout.
                    </p>
                    <button
                        onClick={onboard}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg brut bg-[#FFE55C] font-bold text-sm"
                        data-testid="connect-continue"
                    >
                        <ExternalLink className="w-4 h-4" /> Continue with Stripe
                    </button>
                </>
            )}

            {ready && (
                <>
                    <p className="text-sm text-[#4A4A4A] font-medium mb-3">
                        You&apos;re all set. Payouts arrive in your bank in 2 business days.
                    </p>
                    {status?.schedule && (
                        <div className="brut-inset bg-[#FFFDF7] rounded-lg p-3 mb-3 text-xs font-medium" data-testid="payout-schedule">
                            <span className="font-black uppercase tracking-wider">Schedule:</span>{" "}
                            {status.schedule.interval || "auto"}
                            {status.schedule.weekly_anchor ? ` · every ${status.schedule.weekly_anchor}` : ""}
                            {status.schedule.monthly_anchor ? ` · day ${status.schedule.monthly_anchor}` : ""}
                            {" · "}
                            <a href={status.dashboard_url} target="_blank" rel="noopener noreferrer" className="underline font-bold">
                                change on Stripe →
                            </a>
                        </div>
                    )}
                    <button
                        onClick={instantPayout}
                        disabled={busy || (user?.tip_balance_usd || 0) < 10}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg brut bg-[#FFE55C] font-bold text-sm disabled:opacity-50"
                        data-testid="instant-payout"
                    >
                        <Zap className="w-4 h-4" /> Instant payout — ${(user?.tip_balance_usd || 0).toFixed(2)}
                    </button>
                </>
            )}
        </section>
    );
}
